import { Button } from "@cvpartner/design-system";
import {
  Mail01,
  MessageTextSquare02,
  RefreshCcw01,
} from "@cvpartner/design-system/icons/line";
import * as Sentry from "@sentry/react";
import * as React from "react";
import { useRouteError } from "react-router";
import {
  useIntercomAvailable,
  useIntercomMessage,
} from "@/shared/ui/IntercomHost.tsx";
import { FetchError } from "@/shared/util/fetch.tsx";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { I18n } from "@/shared/util/translations.ts";
import { Forbidden } from "./Forbidden.tsx";
import { OldBrowser } from "./OldBrowser.tsx";
import { errorIcon } from "./Styles.css.ts";
import { Wrapper } from "./Wrapper.tsx";
import lookoutMan from "./lookout-man.svg";

const ChatSupportButton: React.FC = () => {
  const showIntercomMessage = useIntercomMessage();

  return (
    <Button
      hierarchy="secondaryColor"
      leadingIcon={MessageTextSquare02}
      onPress={() => {
        showIntercomMessage("Hi!");
      }}
    >
      {I18n.t("signin.link_contact_support")}
    </Button>
  );
};

const EmailSupportButton: React.FC = () => (
  <Button
    hierarchy="secondaryColor"
    leadingIcon={Mail01}
    variant="link"
    href="mailto:customersuccess@flowcase.com"
  >
    {I18n.t("signin.link_contact_support")}
  </Button>
);

export const ApplicationError: React.FC<{ showHeader?: boolean }> = ({
  showHeader,
}) => {
  const intercomAvailable = useIntercomAvailable();

  return (
    <Wrapper
      showHeader={showHeader}
      heading={I18n.t("unexpected_application_error")}
      description={I18n.t("error_messages.refresh")}
      buttons={
        <>
          <div>
            <Button
              leadingIcon={RefreshCcw01}
              onPress={() => {
                window.location.reload();
              }}
            >
              {I18n.t("button.refresh_page")}
            </Button>
          </div>
          <div>
            {intercomAvailable ? <ChatSupportButton /> : <EmailSupportButton />}
          </div>
        </>
      }
      icon={<img className={errorIcon} src={lookoutMan} aria-hidden={true} />}
      iconPosition="below"
    />
  );
};

const isOldBrowser = typeof Array.prototype.toSorted !== "function";

export const RouteError: React.FC = () => {
  const error = useRouteError();
  const forbidden = error instanceof FetchError && error.status === 403;

  React.useEffect(() => {
    if (error == null || typeof error !== "object" || forbidden) {
      return;
    }

    getCvPartner().faro?.api.pushError(error as Error);

    if (isOldBrowser) {
      Sentry.withScope((scope) => {
        scope.setLevel("warning");
        scope.setExtra("isOldBrowser", isOldBrowser);

        scope.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  }, [error, forbidden]);

  if (isOldBrowser) {
    return <OldBrowser />;
  }

  // Lacking rights is an answer, not a failure, so it is not reported.
  if (forbidden) {
    return <Forbidden />;
  }

  return <ApplicationError />;
};
